import { useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import type { ReturnBody, ShopOrderBody } from '@/shared/api/generated/model';
import { shopDecideReturn, shopPartialRefund } from '@/shared/api/generated/shop/shop';
import { ApiError } from '@/shared/api/http';
import { describeWhen } from '@/shared/lib/dates';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Dialog, DialogContent } from '@/shared/ui/Dialog';
import { Field } from '@/shared/ui/Field';
import { Input } from '@/shared/ui/Input';
import { Switch } from '@/shared/ui/Switch';
import { toast } from '@/shared/ui/Toast';

import styles from './ShopPage.module.scss';
import { money, RETURN_STATUS, returnItemsLabel, returnSum } from './shop';

/** Что открыто: решение по заявке, отказ или возврат части без заявки. */
type Open = { kind: 'refund' | 'reject'; ret: ReturnBody } | { kind: 'partial' };

/**
 * Возвраты заказа (шаг 9.14): заявки покупателя — вернуть деньги или
 * отказать — и возврат части без заявки. Деньги уходят тем же путём, что
 * пришли; без онлайн-оплаты — только запись, вернуть их нужно самим.
 */
export function ReturnsBlock({
  recordId,
  order,
  canRefund,
}: {
  recordId: string;
  order: ShopOrderBody;
  canRefund: boolean;
}) {
  const [open, setOpen] = useState<Open>();
  const returns = order.returns ?? [];
  const active = order.paid_at !== undefined && !order.released;

  if (returns.length === 0 && !(active && canRefund)) {
    return null;
  }

  const close = () => {
    setOpen(undefined);
  };

  return (
    <div className={styles['returns']}>
      <div className={styles['panel__head']}>
        <h3 className={styles['returns__title']}>Возвраты</h3>
        {order.refunded > 0 && (
          <Badge size="sm" tone="danger">
            возвращено {money(order.refunded, order.currency)}
          </Badge>
        )}
        {active && canRefund && (
          <div className={styles['panel__end']}>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setOpen({ kind: 'partial' });
              }}
            >
              Вернуть часть
            </Button>
          </div>
        )}
      </div>

      {returns.length === 0 ? (
        <p className={styles['muted']}>
          Заявок нет. Покупатель подаёт их с сайта; вернуть часть можно и без заявки.
        </p>
      ) : (
        <ul className={styles['payments']}>
          {returns.map((r) => (
            <li key={r.id} className={styles['return']}>
              <div className={styles['payment']}>
                <Badge size="sm" tone={RETURN_STATUS[r.status].tone}>
                  {RETURN_STATUS[r.status].label}
                </Badge>
                {returnItemsLabel(r.items ?? [])}
                {r.status === 'refunded' && ` · ${money(r.amount, order.currency)}`}
                {r.restocked && ' · на складе'}
                {r.manual && r.amount > 0 && ' · вернуть самим'}
                {` · ${describeWhen(r.created_at)}`}
              </div>
              {r.reason !== undefined && r.reason !== '' && (
                <div className={styles['muted']}>«{r.reason}»</div>
              )}
              {r.comment !== undefined && r.comment !== '' && (
                <div className={styles['muted']}>Ответ: {r.comment}</div>
              )}
              {r.status === 'requested' && canRefund && active && (
                <div className={styles['panel__actions']}>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setOpen({ kind: 'refund', ret: r });
                    }}
                  >
                    Вернуть деньги
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setOpen({ kind: 'reject', ret: r });
                    }}
                  >
                    Отказать
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {open !== undefined && (
        <DecideDialog recordId={recordId} order={order} open={open} onClose={close} />
      )}
    </div>
  );
}

function DecideDialog({
  recordId,
  order,
  open,
  onClose,
}: {
  recordId: string;
  order: ShopOrderBody;
  open: Open;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const lines = order.items ?? [];
  const partial = open.kind === 'partial';
  const reject = open.kind === 'reject';

  const [qty, setQty] = useState<string[]>(() => lines.map(() => ''));
  const chosen = partial
    ? lines
        .map((line, i) => ({ line, qty: Number(qty[i] ?? '') }))
        .filter((c) => Number.isInteger(c.qty) && c.qty > 0)
    : [];
  const suggested = partial
    ? returnSum(chosen.map((c) => ({ price: c.line.price, qty: c.qty })))
    : returnSum(open.ret.items ?? []);

  const [amount, setAmount] = useState<string>();
  const [restock, setRestock] = useState(true);
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  const left = Math.max(
    Math.round(
      ((order.payments ?? []).find((p) => p.status === 'succeeded')?.amount ?? order.amount) * 100 -
        order.refunded * 100,
    ) / 100,
    0,
  );
  const value = amount ?? String(Math.min(suggested, left));

  const submit = (event: FormEvent) => {
    event.preventDefault();

    const sum = Number(value.replace(',', '.'));

    if (!reject && !(sum >= 0)) {
      setError('Сумма — число от 0');

      return;
    }

    if (partial && chosen.length === 0 && !(sum > 0)) {
      setError('Выберите позиции или укажите сумму');

      return;
    }

    const bad = partial
      ? lines.find((line, i) => {
          const n = Number(qty[i] ?? '');

          return (qty[i] ?? '') !== '' && !(Number.isInteger(n) && n >= 0 && n <= line.returnable);
        })
      : undefined;

    if (bad !== undefined) {
      setError(`«${bad.title}»: вернуть можно не больше ${String(bad.returnable)} шт.`);

      return;
    }

    setBusy(true);
    setError(undefined);

    const request = partial
      ? shopPartialRefund(recordId, {
          items: chosen.map((c) => ({
            product: c.line.product,
            ...(c.line.variant ? { variant: c.line.variant } : {}),
            qty: c.qty,
          })),
          amount: sum,
          restock: restock && chosen.length > 0,
          comment: comment.trim(),
        })
      : shopDecideReturn(recordId, open.ret.id, {
          refund: !reject,
          ...(reject ? {} : { amount: sum, restock: restock && (open.ret.items ?? []).length > 0 }),
          comment: comment.trim(),
        });

    request
      .then((response) => {
        if (response.status === 200) {
          queryClient.setQueryData(['shop-order', recordId], response.data);
        }

        toast.success(reject ? 'Отказано' : 'Возврат записан');
        onClose();
      })
      .catch((cause: unknown) => {
        setError(cause instanceof ApiError ? cause.detail : 'Не удалось записать возврат');
      })
      .finally(() => {
        setBusy(false);
      });
  };

  const hasItems = partial ? chosen.length > 0 : (open.ret.items ?? []).length > 0;
  const title = reject
    ? 'Отказать в возврате'
    : partial
      ? 'Вернуть часть'
      : `Вернуть: ${returnItemsLabel(open.ret.items ?? [])}`;

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) {
          onClose();
        }
      }}
    >
      <DialogContent
        title={title}
        size="sm"
        dismissible={false}
        footer={
          <>
            <Button variant="ghost" onClick={onClose}>
              Отмена
            </Button>
            <Button
              type="submit"
              form="return-form"
              tone={reject ? 'accent' : 'danger'}
              loading={busy}
            >
              {reject ? 'Отказать' : 'Вернуть деньги'}
            </Button>
          </>
        }
      >
        <form id="return-form" className={styles['form']} onSubmit={submit} noValidate>
          {partial && (
            <div className={styles['return-lines']}>
              {lines.map((line, i) => (
                <label
                  key={`${line.product}/${line.variant ?? ''}`}
                  className={styles['return-line']}
                >
                  <span>
                    {line.title}
                    {line.options ? ` (${line.options})` : ''}
                    <span className={styles['muted']}>
                      {' '}
                      · {money(line.price, order.currency)} · можно {String(line.returnable)} из{' '}
                      {String(line.qty)}
                    </span>
                  </span>
                  <Input
                    inputMode="numeric"
                    aria-label={`Сколько вернуть: ${line.title}`}
                    placeholder="0"
                    disabled={line.returnable === 0}
                    value={qty[i] ?? ''}
                    onChange={(event) => {
                      setQty(qty.map((q, j) => (j === i ? event.target.value : q)));
                      setAmount(undefined);
                    }}
                  />
                </label>
              ))}
            </div>
          )}

          {!reject && (
            <Field
              label={`Сколько вернуть, ${order.currency === 'RUB' ? '₽' : order.currency}`}
              hint={`По ценам заказа — ${money(suggested, order.currency)}. Можно вернуть ещё ${money(left, order.currency)}.`}
            >
              <Input
                inputMode="decimal"
                value={value}
                onChange={(event) => {
                  setAmount(event.target.value);
                }}
              />
            </Field>
          )}

          {!reject && hasItems && (
            <Switch checked={restock} onCheckedChange={setRestock}>
              Товар вернулся — положить на склад
            </Switch>
          )}

          <Field
            label={reject ? 'Причина отказа' : 'Комментарий'}
            hint="Покупатель увидит его у заказа"
          >
            <Input
              value={comment}
              onChange={(event) => {
                setComment(event.target.value);
              }}
            />
          </Field>

          {!reject && (
            <p className={styles['muted']}>
              Деньги уходят тем же путём, что пришли. Оплата была не онлайн — здесь только запись,
              вернуть деньги нужно самим. Отменить возврат нельзя.
            </p>
          )}
          {error !== undefined && <p className={styles['error']}>{error}</p>}
        </form>
      </DialogContent>
    </Dialog>
  );
}
